import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { configureAdminApi } from '@/lib/api'

const STORAGE_KEY = 'agrosdr.admin.credentials'

type AdminCredentials = {
  apiUrl: string
  token: string
}

type AdminAuthContextValue = {
  credentials: AdminCredentials | null
  isConfigured: boolean
  setCredentials: (credentials: AdminCredentials) => void
  clearCredentials: () => void
}

const AdminAuthContext = createContext<AdminAuthContextValue | undefined>(
  undefined
)

function readStoredCredentials(): AdminCredentials | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<AdminCredentials>
    if (!parsed.apiUrl || !parsed.token) return null
    return { apiUrl: parsed.apiUrl, token: parsed.token }
  } catch {
    return null
  }
}

function writeStoredCredentials(credentials: AdminCredentials | null) {
  try {
    if (credentials) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(credentials))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // Locked-down browsers can reject localStorage; the in-memory session still works.
  }
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [credentials, setCredentialsState] = useState<AdminCredentials | null>(
    () => {
      const stored = readStoredCredentials()
      configureAdminApi(stored ?? {})
      return stored
    }
  )

  const setCredentials = useCallback((next: AdminCredentials) => {
    const normalized = {
      apiUrl: next.apiUrl.trim().replace(/\/$/, ''),
      token: next.token.trim(),
    }
    setCredentialsState(normalized)
    writeStoredCredentials(normalized)
    configureAdminApi(normalized)
  }, [])

  const clearCredentials = useCallback(() => {
    setCredentialsState(null)
    writeStoredCredentials(null)
    configureAdminApi({})
  }, [])

  const value = useMemo(
    () => ({
      credentials,
      isConfigured: Boolean(credentials?.apiUrl && credentials.token),
      setCredentials,
      clearCredentials,
    }),
    [clearCredentials, credentials, setCredentials]
  )

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext)
  if (!context) {
    throw new Error('useAdminAuth must be used inside AdminAuthProvider')
  }
  return context
}
